import { useCallback, useEffect } from 'react';
import { Resetter, atom, useRecoilState, useResetRecoilState } from 'recoil';
import { localStorageEffect } from 'libs/recoil';
import { LOCAL_STORAGE_KEY } from 'state/localStorage';

export type DonationState = {
  donationAmount: number;
  donationRenews: boolean;
  optInToUpdates: boolean;
  existingUser: boolean;
  email: string;
  firstName: string;
  lastName: string;
  fromMobile: boolean;
  updatePaymentMethod: boolean;
  clientToken?: string;
  verificationToken?: string;
  userId?: number;
};

type UseDonationState = DonationState & {
  updateDonationState: (newState: Partial<DonationState>) => void;
  resetDonationState: Resetter;
};

type UseDonationStateProps = {
  /** State coming from an external source (e.g. URL) */
  stateUpdateData?: Partial<DonationState>;
};

export const DEFAULT_DONATION_STATE: DonationState = {
  donationAmount: 0,
  donationRenews: false,
  optInToUpdates: false,
  existingUser: false,
  email: '',
  firstName: '',
  lastName: '',
  fromMobile: false,
  updatePaymentMethod: false,
};

const donationStateAtom = atom({
  key: LOCAL_STORAGE_KEY.DONATION_STATE,
  default: DEFAULT_DONATION_STATE,
  effects: [localStorageEffect(LOCAL_STORAGE_KEY.DONATION_STATE)],
});

export const useDonationState = (
  props: UseDonationStateProps = {},
): UseDonationState => {
  const { stateUpdateData } = props;
  const resetDonationState = useResetRecoilState(donationStateAtom);
  const [donationState, setDonationState] = useRecoilState(donationStateAtom);

  const updateDonationState = useCallback(
    (newState: Partial<DonationState>) => {
      setDonationState((prevState) => ({ ...prevState, ...newState }));
    },
    [setDonationState],
  );

  /**
   * Synchronize external state (e.g. URL) to Recoil asynchronously.
   * The merge below provides immediate access while this update completes.
   */
  useEffect(() => {
    if (stateUpdateData) {
      updateDonationState(stateUpdateData);
    }
  }, [stateUpdateData, updateDonationState]);

  /**
   * Merge the current Recoil donation state with stateUpdateData.
   *
   * Flow behavior:
   * - In-app flow: stateUpdateData is undefined, so the existing Recoil state
   *   remains the single source of truth.
   * - External browser flow: stateUpdateData contains incoming donation data
   *   parsed from the URL. This ensures consumers receive the most recent
   *   donation values even before the asynchronous Recoil update completes.
   */
  return {
    ...donationState,
    ...(stateUpdateData ?? {}),
    updateDonationState,
    resetDonationState,
  };
};
